import Player from 'youtube-player';

const BREAKPOINT_SP = 768;
const IFRAME_SELECTOR_NAME = '.js-youtube-iframe';
const SOUND_SELECTOR_NAME = '.js-youtube-sound';
const PLAY_SELECTOR_NAME = '.js-youtube-play';
const PLAY_CONTROLL_SELECTOR_NAME = '.js-youtube-play-controll';
const END_GUIDANCE_SELECTOR_NAME = '.js-youtube-end-guidance';
const SEEK_SELECTOR_NAME = '[data-seek]';

const PLAY_CLASS = '-play';
const ON_CLASS = '-on';
const HIDE_CLASS = '-hide';

/**
 * YouTubePlayer class
 * @sample
  <div class="js-youtube">
    <div class="js-youtube-iframe" id="youtube001" data-option="{
      videoId: '',
      playerVars: {
        autoplay: 1,
        loop: 1,
      }
    }"></div>
  </div>
 */
export default class YouTubePlayer {
  /**
   * [constructor description]
   * @return {[type]} [description]
   */
  constructor(el) {
    this.el = el;
    this.iframeEl = this.el.querySelector(IFRAME_SELECTOR_NAME);
    this.soundEl = this.el.querySelector(SOUND_SELECTOR_NAME);
    this.playEl = this.el.querySelector(PLAY_SELECTOR_NAME);
    this.playControllEl = this.el.querySelector(PLAY_CONTROLL_SELECTOR_NAME);
    this.seekEl = this.el.querySelectorAll(SEEK_SELECTOR_NAME);
    this.endGuidanceEl = document.querySelectorAll(END_GUIDANCE_SELECTOR_NAME);
    this.endSecondsAgo = this.iframeEl.dataset.endSecondsAgo
      ? Number(this.iframeEl.dataset.endSecondsAgo)
      : 0;
    this.player = null;
    this.loop = false;
    this.autoplay = false;
    this.duration = 0;
    this.isFullscreenSP = false;
    this.playlist = null;
    this.playerVars = {
      autoplay: 0,
      playsinline: 1,
      loop: 0,
      showinfo: 0,
      rel: 0,
      fs: 0
    };

    this.init();
  }

  async requestAnimationFrame() {
    const currentTime = await this.player.getCurrentTime();
    if (this.loop && this.autoplay && currentTime >= this.duration - 1) {
      this.replay();
    }
    window.requestAnimationFrame(this.requestAnimationFrame.bind(this));
  }

  requestFullScreen() {
    if (this.isFullscreenSP && window.innerWidth < BREAKPOINT_SP) {
      const requestFullScreen =
        this.iframeEl.requestFullScreen ||
        this.iframeEl.mozRequestFullScreen ||
        this.iframeEl.webkitRequestFullScreen;
      if (requestFullScreen) {
        requestFullScreen.bind(this.iframeEl)();
      }
    }
  }

  exitFullscreen() {
    if (this.isFullscreenSP && window.innerWidth < BREAKPOINT_SP) {
      const exitFullscreen =
        document.exitFullscreen ||
        document.mozCancelFullScreen ||
        document.webkitExitFullscreen;
      if (exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  async init() {
    // data-optionに設定されているオプションとデフォルトのオプションをマージする
    const option = this.iframeEl.dataset.option
      ? new Function('return ' + this.iframeEl.dataset.option)()
      : {};
    this.playerVars = Object.assign(this.playerVars, option.playerVars);

    // 標準のループだと読み込みアニメーションが発生するため、
    // 変数に保存しループは無効化しておく
    // 再生が終わったらシークを0にすることでループに対応
    this.loop = this.playerVars.loop > 0;
    this.playerVars.loop = 0;

    // オプション値を更新
    this.autoplay = this.playerVars.autoplay > 0;
    this.isFullscreenSP = option.isFullscreenSP;

    option.playerVars = this.playerVars;

    if (!option.videoId) return;

    this.player = new Player(this.iframeEl.getAttribute('id'), option);
    this.player.on('ready', () => this.ready());
    this.player.on('stateChange', (ev) => this.stateChange(ev));

    this.iframeEl = await this.player.getIframe();
    this.playlist = await this.player.getPlaylist();

    if (this.loop) {
      this.requestAnimationFrame();
    }

    this.eventHandler();
    this.startEndGuidance();
  }

  eventHandler() {
    if (this.soundEl) {
      this.soundEl.addEventListener('click', this.onSound.bind(this));
    }
    if (this.playControllEl) {
      this.playControllEl.addEventListener(
        'click',
        this.onPlayControll.bind(this)
      );
    }
    if (this.playEl) {
      this.playEl.addEventListener('click', this.replay.bind(this));
    }
    this.seekEl.forEach((el) => {
      el.addEventListener('click', this.seek.bind(this));
    });
  }

  /**
   * 終了後の誘導を表示させる関数
   */
  async startEndGuidance() {
    const currentTime = await this.player.getCurrentTime();
    const duration = await this.player.getDuration();
    const playlistIndex = await this.player.getPlaylistIndex();

    // 動画時間が取れなければススキップ
    if (!duration) {
      window.requestAnimationFrame(this.startEndGuidance.bind(this));
      return;
    }

    // プレイリストの場合最後の動画でない場合はスキップ
    if (this.playlist) {
      if (this.playlist.length - 1 !== playlistIndex) {
        window.requestAnimationFrame(this.startEndGuidance.bind(this));
        return;
      }
    }

    if (duration - this.endSecondsAgo <= currentTime) {
      this.endGuidanceEl.forEach((el) => {
        el.classList.remove(HIDE_CLASS);
      });
    }

    window.requestAnimationFrame(this.startEndGuidance.bind(this));
  }

  onSound(ev) {
    ev.preventDefault();
    if (this.soundEl.classList.contains(ON_CLASS)) {
      this.soundEl.classList.remove(ON_CLASS);
      this.player.mute();
    } else {
      this.soundEl.classList.add(ON_CLASS);
      this.player.unMute();
    }
  }

  async onPlayControll() {
    const state = await this.player.getPlayerState();
    if (state === 1) {
      this.player.pauseVideo();
    } else if (state === 2) {
      this.play();
    }
  }

  async ready() {
    this.duration = await this.player.getDuration();

    if (this.autoplay) {
      // this.player.mute();
      this.play();
    }
  }

  stateChange(ev) {
    // requestAnimationFrameでのループが失敗したときの保険でいれておく
    if (ev.data === 0 && this.loop) {
      this.player.seekTo(0, true);
      this.play();
    }
    // 再生中
    if (ev.data === 1) {
      this.el.classList.add(PLAY_CLASS);
    }
    // 停止
    if (ev.data === 2) {
      this.exitFullscreen();
    }
    // 終了、停止
    if (ev.data === 0 || ev.data === 2) {
      this.el.classList.remove(PLAY_CLASS);
    }
  }

  play() {
    this.requestFullScreen();
    this.player.playVideo();
  }

  replay() {
    this.player.seekTo(0, true);
    this.play();
  }

  async seek(ev) {
    ev.preventDefault();
    const seek = ev.currentTarget.dataset.seek;
    const index = ev.currentTarget.dataset.index;
    let minute = 0;
    let second = 0;
    let seekSeconds = 0;

    if (/:/.test(seek)) {
      [minute, second] = seek.split(':');
      seekSeconds = minute * 60 + Number(second);
    } else {
      seekSeconds = Number(seek);
    }

    if (index >= 0 && this.playlist) {
      this.player.playVideoAt(index);
      this.player.loadVideoById(this.playlist[index], seekSeconds);
    } else {
      this.player.seekTo(seekSeconds, true);
      this.play();
    }
  }
}
